import { Text, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { type GeoLocation } from "@src/appV2/Location";
import { useCallback } from "react";

import { Button } from "../../../components/Button";
import { InlineAlert } from "../../../components/InlineAlert";
import { PageFooterWrapper } from "../../../components/PageFooterWrapper";
import { TextButton } from "../../../components/TextButton";
import { ReturnForSignatureBottomSheet } from "../../components/ReturnForSignatureBottomSheet";
import { useMissedPunchRequestGeofenceCheck } from "../../hooks/useMissedPunchRequestGeofenceCheck";
import { UploadTimesheetBottomSheet } from "../../UnverifiedShifts/UploadTimesheet/UploadTimesheetBottomSheet";
import { type FooterDataProps, getFooterContent } from "./utils/getFooterContent";

interface PastShiftDetailsFooterProps extends FooterDataProps {
  shiftId: string;
  shiftEnd: string;
  workerId: string;
  workplaceLocation: GeoLocation | undefined;
  requestId?: string;
  navigateToSubmitTimesheet: (skipWorkplaceSignature: boolean) => void;
  dismissRequest: (requestId: string) => void;
}

export function PastShiftDetailsFooter(props: PastShiftDetailsFooterProps) {
  const {
    shiftId,
    shiftEnd,
    workerId,
    workplaceLocation,
    missedPunchRequestStatus,
    isVerified,
    isFullShiftPayment,
    isMissedPunchRequestEnabled,
    isTimesheetUploaded,
    canSubmitTimesheet,
    requestExpiryTime,
    facilityTimeZone,
    requestId,
    navigateToSubmitTimesheet,
    dismissRequest,
  } = props;

  const footerDataProps: FooterDataProps = {
    missedPunchRequestStatus,
    isVerified,
    isFullShiftPayment,
    isMissedPunchRequestEnabled,
    isTimesheetUploaded,
    canSubmitTimesheet,
    requestExpiryTime,
    facilityTimeZone,
  };

  const uploadTimesheetModalState = useModalState();
  const returnForSignatureModalState = useModalState();
  const { isCheckingGeofence, checkGeofence } = useMissedPunchRequestGeofenceCheck();

  const handleSubmitNewRequest = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_FOOTER_ACTION_TAPPED, {
      shiftId,
      action: "submit_new_request",
    });

    const result = await checkGeofence({
      shiftId,
      shiftEnd,
      workerId,
      workplaceLocation,
    });

    if (result.status === "inside" || result.status === "unknown") {
      navigateToSubmitTimesheet(false);
    } else {
      logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
        shiftId,
        workerId,
      });
      returnForSignatureModalState.openModal();
    }
  }, [
    checkGeofence,
    navigateToSubmitTimesheet,
    returnForSignatureModalState,
    shiftEnd,
    shiftId,
    workerId,
    workplaceLocation,
  ]);

  const handleUploadTimesheet = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_FOOTER_ACTION_TAPPED, {
      shiftId,
      action: "submit_timesheet",
    });
    uploadTimesheetModalState.openModal();
  }, [shiftId, uploadTimesheetModalState]);

  const handleAcceptOriginalTimes = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_FOOTER_ACTION_TAPPED, {
      shiftId,
      action: "accept_original_times",
    });

    if (isDefined(requestId)) {
      dismissRequest(requestId);
    }
  }, [shiftId, requestId, dismissRequest]);

  const handleContactSupport = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_FOOTER_ACTION_TAPPED, {
      shiftId,
      action: "contact_support",
    });
    void openZendeskMessaging();
  }, [shiftId]);

  const handleRecheckLocation = useCallback(async () => {
    const result = await checkGeofence({
      shiftId,
      shiftEnd,
      workerId,
      workplaceLocation,
    });

    if (result.status === "inside" || result.status === "unknown") {
      returnForSignatureModalState.closeModal();
      navigateToSubmitTimesheet(false);
    }
  }, [
    checkGeofence,
    navigateToSubmitTimesheet,
    returnForSignatureModalState,
    shiftEnd,
    shiftId,
    workerId,
    workplaceLocation,
  ]);

  const handleConfirmWithoutSignature = useCallback(() => {
    navigateToSubmitTimesheet(true);
  }, [navigateToSubmitTimesheet]);

  const handleCancelReturnForSignature = useCallback(() => {
    returnForSignatureModalState.closeModal();
  }, [returnForSignatureModalState]);

  const content = getFooterContent(footerDataProps, {
    onSubmitNewRequest: handleSubmitNewRequest,
    onAcceptOriginalTimes: handleAcceptOriginalTimes,
    onUploadTimesheet: handleUploadTimesheet,
    onContactSupport: handleContactSupport,
  });

  if (!isDefined(content)) {
    return null;
  }

  return (
    <>
      <PageFooterWrapper withShadow sx={content.wrapperSx}>
        <Stack spacing={4} alignItems="center">
          {isDefined(content.alertTitle) && (
            <InlineAlert
              iconType="triangle-exclamation"
              iconColor={(theme) => theme.palette.intent?.error.text}
              title={content.alertTitle}
              titleColor={(theme) => theme.palette.intent?.error.text}
              description={content.description}
              descriptionVariant={content.descriptionVariant}
            />
          )}

          {!isDefined(content.alertTitle) && (
            <Text
              semibold
              variant={content.descriptionVariant}
              align="center"
              sx={{ color: "text.secondary" }}
            >
              {content.description}
            </Text>
          )}

          <Button
            fullWidth
            disabled={content.primaryButtonDisabled}
            variant={content.primaryButtonVariant}
            size="large"
            startIconType={content.primaryButtonStartIconType}
            onClick={content.primaryButtonAction}
          >
            {content.primaryButtonLabel}
          </Button>

          {isDefined(content.secondaryButtonLabel) && isDefined(content.secondaryButtonAction) && (
            <TextButton size="medium" onClick={content.secondaryButtonAction}>
              {content.secondaryButtonLabel}
            </TextButton>
          )}
        </Stack>
      </PageFooterWrapper>

      <UploadTimesheetBottomSheet
        modalState={uploadTimesheetModalState}
        shiftId={shiftId}
        isTimesheetUploaded={isTimesheetUploaded}
        onUploadStarted={() => {
          logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_UPLOAD_TIMESHEET_STARTED, {
            shiftId,
            workerId,
          });
        }}
        onSuccess={({ uploadDurationInMs, updateTimecardDurationInMs }) => {
          logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_UPLOAD_TIMESHEET_UPLOADED, {
            shiftId,
            workerId,
            isReupload: isTimesheetUploaded,
            uploadDurationInMs,
            updateTimecardDurationInMs,
          });
        }}
        onError={(error) => {
          logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_UPLOAD_TIMESHEET_UPLOAD_ERROR, {
            shiftId,
            workerId,
            error: error?.message ?? "Unknown error",
          });
        }}
      />

      <ReturnForSignatureBottomSheet
        modalState={returnForSignatureModalState}
        shiftId={shiftId}
        workerId={workerId}
        isCheckingLocation={isCheckingGeofence}
        isShiftVerified={isVerified}
        onRecheckLocation={handleRecheckLocation}
        onCancel={handleCancelReturnForSignature}
        onConfirm={handleConfirmWithoutSignature}
      />
    </>
  );
}
